"use client";

import { type FC, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Avatar,
  type AvatarProps,
  Button,
  Flex,
  Icon,
  Popup,
  Select,
  Spacing,
  Text,
  Tooltip,
  useClickOutside,
} from "@huub-web/ui";
import { useNavigationState } from "../../providers";
import { useAuth } from "@huub-web/backend";

import { useNavigate, useAppController, useAppSettings } from "../../hooks";
import { getAvailableLocales, LOCALE_LABELS } from "../../config/locales";
import { DesktopNavigation } from "./DesktopNavigation";
import { MobileNavigation } from "./MobileNavigation";

import { avatarPopupContentStyle } from "./Navigation.css";

/**
 * Main navigation component that branches between desktop and mobile layouts.
 * Merges the old NavigationController logic (auth, avatar popup, locale switching)
 * with the FixedNavigation routing logic.
 */
export const Navigation: FC = () => {
  const { t, i18n } = useTranslation();
  const { navigate } = useNavigate();
  const locale = i18n.language;
  const setLocale = (locale: string) => i18n.changeLanguage(locale);
  const { indentity, logout } = useAuth();
  const { user } = useAppController();
  const { theme } = useAppSettings();
  const availableLocales = getAvailableLocales(theme);

  const { setNavigationState, getNavigationPath, menuVariant } =
    useNavigationState();

  const [isAvatarPopupActive, setAvatarPopupActive] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);
  const avatarPopupRef = useRef<HTMLDivElement>(null);

  const avatar: Pick<AvatarProps, "value" | "image"> = (() => {
    if (user && (user.name || user.email)) {
      return { value: user.name ?? user.email ?? "onbekend", id: user.id };
    }
    if (indentity) {
      return { value: indentity.name ?? indentity.email ?? "onbekend" };
    }
    return { value: "onbekend" };
  })();

  const handleNavigate = (key: string) => {
    const url = getNavigationPath(key);
    if (url) {
      navigate(url);
    }
    setNavigationState(key);
  };

  useClickOutside([avatarPopupRef], () => closeAvatarPopup());
  const closeAvatarPopup = () => setAvatarPopupActive(false);

  /**
   * Locale options for the language dropdown
   */
  const localeOptions = availableLocales.map((loc: string) => ({
    value: loc,
    label: LOCALE_LABELS[loc] ?? loc,
  }));

  const handleLogout = () => {
    closeAvatarPopup();
    logout();
  };

  const avatarPopup = avatar && (
    <Popup
      ref={avatarPopupRef}
      targetRef={avatarRef}
      active={isAvatarPopupActive}
      placement="rightTop"
      onClose={closeAvatarPopup}
      minWidth="200px"
      maxWidth="240px"
      escape
      role="dialog"
      content={
        <Flex
          direction="column"
          className={avatarPopupContentStyle}
          align="center"
          gap="xs"
        >
          <Text color="currentColor50" clamp={2}>
            {t("auth.as", { name: avatar.value ?? "onbekend" })}
          </Text>
          {availableLocales.length > 1 && (
            <Select
              value={locale}
              options={localeOptions}
              onChange={(value) => setLocale(value as string)}
              leading={<Icon icon="world" size="s" />}
            />
          )}
          <Spacing />
          <Button
            variant="secondary"
            size="s"
            trailing={<Icon icon="logOut" size="s" />}
            onClick={handleLogout}
            width="stretch"
          >
            {t("auth.logout.button")}
          </Button>
        </Flex>
      }
    >
      <Avatar
        ref={avatarRef}
        variant="secondary"
        size="menu"
        onClick={() => setAvatarPopupActive(true)}
        aria-labelledby="avatarProfileLabel"
        {...avatar}
      />
      <Tooltip
        id="avatarProfileLabel"
        value={t("general.profile")}
        placement="right"
        targetRef={avatarRef}
      />
    </Popup>
  );

  return menuVariant === "compact" ? (
    <MobileNavigation
      onNavigate={handleNavigate}
      onLogout={handleLogout}
      theme={theme}
    />
  ) : (
    <DesktopNavigation
      onNavigate={handleNavigate}
      trailing={avatarPopup}
      theme={theme}
    />
  );
};
